import { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useAppRouter } from '../../useAppRouter';
import { PageAppletCapability, type Specs } from '@doxyme/extensions-bridge';
import {
  useAddCapability,
  useRegistrySingleton,
} from '@doxyme/extensions-bridge-react';
import { getPublicRuntimeConfig } from '../../../libs/init/getPublicRuntimeConfig';
import { bridge } from '../../../features/extensions/instances';
import { useNavigateToPreviousRoute } from '../../usePreviousRoute';
import { useExtensionsNavigationInterception } from '../useExtensionsNavigationInterception';
import { getLogger } from '../../../libs/logger';

const { paths } = getPublicRuntimeConfig();

const logger = getLogger('useExtensionsAddPageApplet', {
  component: 'extensions',
  lib: 'frontend',
});

type QueryParamData = Specs.PageApplet.QueryParamData;

/**
 * Hook responsible for providing callbacks required for pageApplet to the Extensions SDK
 */
export function useExtensionsAddPageApplet(): void {
  const router = useAppRouter();
  const { apps } = useRegistrySingleton();

  const pageApplet = useAddCapability(bridge, () => new PageAppletCapability());

  const navigate = useNavigateToPreviousRoute(paths.dashboard);

  const { clearNavigationConfirmationReasons } =
    useExtensionsNavigationInterception();

  const appsRef = useRef(apps);
  appsRef.current = apps;
  const routerRef = useRef(router);
  routerRef.current = router;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const clearReasonsRef = useRef(clearNavigationConfirmationReasons);
  clearReasonsRef.current = clearNavigationConfirmationReasons;

  useEffect(() => {
    const unsubscribes = [
      pageApplet.onOpen(
        async (
          manifestId: string,
          name?: string,
          data?: QueryParamData,
        ): Promise<void> => {
          const app = appsRef.current.find((a) => a.manifest.id === manifestId);

          if (app === undefined) {
            logger.error(
              'App was not found for manifestId, cannot navigate to page applet',
              { manifest: { id: manifestId } },
            );
            return;
          }

          await routerRef.current.push({
            pathname: paths.extensionsPageAppletProvider,
            query: {
              slug: app.slugUrl,
              ...(name !== undefined ? { name } : {}),
              ...(data !== undefined ? { data: JSON.stringify(data) } : {}),
            },
          });
        },
      ),
      pageApplet.onClose(async () => {
        flushSync(() => clearReasonsRef.current());
        await navigateRef.current();
      }),
    ];

    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
    };
  }, [pageApplet]);
}
